<template>
  <textarea
    ref="input"
    :value="formatValue"
    :class="allClasses"
    v-bind="attrs"
    v-on="listeners"
    @input="onInput"
  />
</template>

<script>
import classes from '~~/common/vue/mixins/classes';

export const AUTOSCALE_MAX_HEIGHT = 100;
export const sizes = ['m', 'l'];

export default {
  name: 'ETextarea',
  mixins: [classes],
  props: {
    model: {
      type: String,
      default: null,
    },
    value: {
      type: String,
      default: null,
    },
    name: {
      type: [String, Number],
      default: null,
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    invalid: {
      type: Boolean,
      default: false,
      cssClass: true,
    },
    autoscale: {
      type: Boolean,
      default: false,
      cssClass: true,
    },
    size: {
      type: String,
      default: 'm',
      cssClass: true,
      validator: (value) => sizes.includes(value),
    },
  },
  data() {
    return {
      formatValue: this.value,
      dirty: false,
    };
  },
  computed: {
    attrs() {
      const { name, required, disabled, $attrs } = this;
      return { ...$attrs, name, required, disabled };
    },
    listeners() {
      const { input, ...rest } = this.$listeners;
      return rest;
    },
    allClasses() {
      const allClasses = this.classes;
      if (this.dirty) {
        allClasses.push('textarea--dirty');
      }
      return allClasses;
    },
  },
  watch: {
    value(value) {
      this.formatValue = value;
    },
  },
  methods: {
    onInput({ target: { value } }) {
      this.formatValue = value;
      this.dirty = true;
      this.$emit('input', value);
      if (this.autoscale) {
        this.setHeight();
      }
    },
    async setHeight() {
      this.$el.style.height = '';
      const { scrollHeight } = this.$el;
      const height = Math.min(scrollHeight, AUTOSCALE_MAX_HEIGHT);
      await this.$nextTick();
      this.$el.style.height = `${height}px`;
    },
  },
};
</script>

<style lang="scss" scoped>
$sizes: (
  m: (
    font-size: $font-m,
    padding: $space-s,
    height: $space-3l,
  ),
  l: (
    font-size: $font-l,
    padding: $space-m,
    height: $space-5l,
  )
);

.textarea {
  display: block;
  box-sizing: border-box;
  width: 100%;
  min-height: $space-8l;
  border: solid $border-width var(--color-form-border-default);
  border-radius: $border-radius-l;
  font-size: $font-m;
  font-family: $family;
  line-height: $line-height-s;
  color: var(--color-text-primary);
  background: var(--color-background-primary);
  resize: vertical;
  transition: border $timing-2s $easing;

  &:hover {
    border-color: var(--color-form-border-hover);
  }

  &:focus {
    @extend %keyboard-nav-focus;

    border-color: var(--color-form-border-primary-active);
  }

  &:disabled {
    color: var(--color-disabled);
    background-color: var(--color-background-primary);
  }

  &::placeholder {
    color: var(--color-text-secondary);
    opacity: $opacity-m;
  }
}

.textarea--dirty:invalid,
.textarea--invalid {
  border-color: var(--color-error);
}

@each $name, $map in $sizes {
  .textarea--size-#{$name} {
    font-size: map-get($map, font-size);
    padding: map-get($map, padding);
  }

  .textarea--autoscale {
    height: map-get($map, height);
    min-height: map-get($map, height);
  }
}
</style>
